import React, { createContext, useContext, type ReactNode } from "react";
import confetti from "canvas-confetti";

interface ConfettiContextType {
  fireConfetti: ({ golden, emoji }: { golden?: boolean; emoji?: string }) => void;
}

const ConfettiContext = createContext<ConfettiContextType | undefined>(undefined);

export function useConfetti() {
  const context = useContext(ConfettiContext);
  if (!context) {
    throw new Error("useConfetti must be used within a ConfettiProvider");
  }
  return context;
}

interface ConfettiProviderProps {
  children: ReactNode;
}

export function ConfettiProvider({ children }: ConfettiProviderProps) {
  const fireConfetti = ({ golden, emoji }: { golden?: boolean; emoji?: string }) => {
    const end = Date.now() + 2 * 1000; // 2 seconds
    const colors = golden
      ? ["#FFD700", "#FDB931", "#F4C430", "#DAA520"] // golden colors
      : ["#8B4DFF", "#FF4D9E", "#FF824D", "#9EFF4D"];

    const frame = () => {
      if (Date.now() > end) return;

      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors,
        shapes: emoji ? [confetti.shapeFromText({ text: emoji, scalar: 1 })] : undefined,
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        shapes: emoji ? [confetti.shapeFromText({ text: emoji, scalar: 1 })] : undefined,
        colors: colors,
      });

      requestAnimationFrame(frame);
    };

    frame();
  };

  return <ConfettiContext.Provider value={{ fireConfetti }}>{children}</ConfettiContext.Provider>;
}
